import { Inject, Injectable } from '@angular/core';
import * as moment from 'moment';
import { ENV } from '../../../../environments/environment.provider';
import { Environment } from '../../../../environments/environment.model';

@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  private readonly states = [
    { key: 'AL', label: 'Alabama' },
    { key: 'AK', label: 'Alaska' },
    { key: 'AZ', label: 'Arizona' },
    { key: 'AR', label: 'Arkansas' },
    { key: 'CA', label: 'California' },
    { key: 'CO', label: 'Colorado' },
    { key: 'CT', label: 'Connecticut' },
    { key: 'DE', label: 'Delaware' },
    { key: 'FL', label: 'Florida' },
    { key: 'GA', label: 'Georgia' },
    { key: 'HI', label: 'Hawaii' },
    { key: 'ID', label: 'Idaho' },
    { key: 'IL', label: 'Illinois' },
    { key: 'IN', label: 'Indiana' },
    { key: 'IA', label: 'Iowa' },
    { key: 'KS', label: 'Kansas' },
    { key: 'KY', label: 'Kentucky' },
    { key: 'LA', label: 'Louisiana' },
    { key: 'ME', label: 'Maine' },
    { key: 'MD', label: 'Maryland' },
    { key: 'MA', label: 'Massachusetts' },
    { key: 'MI', label: 'Michigan' },
    { key: 'MN', label: 'Minnesota' },
    { key: 'MS', label: 'Mississippi' },
    { key: 'MO', label: 'Missouri' },
    { key: 'MT', label: 'Montana' },
    { key: 'NE', label: 'Nebraska' },
    { key: 'NV', label: 'Nevada' },
    { key: 'NH', label: 'New Hampshire' },
    { key: 'NJ', label: 'New Jersey' },
    { key: 'NM', label: 'New Mexico' },
    { key: 'NY', label: 'New York' },
    { key: 'NC', label: 'North Carolina' },
    { key: 'ND', label: 'North Dakota' },
    { key: 'OH', label: 'Ohio' },
    { key: 'OK', label: 'Oklahoma' },
    { key: 'OR', label: 'Oregon' },
    { key: 'PA', label: 'Pennsylvania' },
    { key: 'RI', label: 'Rhode Island' },
    { key: 'SC', label: 'South Carolina' },
    { key: 'SD', label: 'South Dakota' },
    { key: 'TN', label: 'Tennessee' },
    { key: 'TX', label: 'Texas' },
    { key: 'UT', label: 'Utah' },
    { key: 'VT', label: 'Vermont' },
    { key: 'VA', label: 'Virginia' },
    { key: 'WA', label: 'Washington' },
    { key: 'WV', label: 'West Virginia' },
    { key: 'WI', label: 'Wisconsin' },
    { key: 'WY', label: 'Wyoming' },
  ];

  private readonly timeZones = [
    { key: 'America/New_York', label: 'Eastern' },
    { key: 'America/Chicago', label: 'Central' },
    { key: 'America/Denver', label: 'Mountain' },
    { key: 'America/Los_Angeles', label: 'Pacific' },
    { key: 'America/Anchorage', label: 'Alaska' },
    { key: 'Pacific/Honolulu', label: 'Hawaii' },
  ];

  private readonly eventResults = [
    { key: 'Win', label: 'Win' },
    { key: 'Loss', label: 'Loss' },
    { key: 'Tie', label: 'Tie' },
    { key: 'DNP', label: 'DNP' },
    { key: 'Split', label: 'Split' },
    { key: 'First', label: 'First' },
    { key: 'Second', label: 'Second' },
    { key: 'Third', label: 'Third' },
    { key: 'Fourth', label: 'Fourth' },
    { key: 'Fifth', label: 'Fifth' },
    { key: 'Sixth', label: 'Sixth' },
    { key: 'Seventh', label: 'Seventh' },
    { key: 'Eighth', label: 'Eighth' },
    { key: 'Ninth', label: 'Ninth' },
    { key: 'Tenth', label: 'Tenth' },
    { key: 'Eleventh', label: 'Eleventh' },
    { key: 'Twelfth', label: 'Twelfth' },
    { key: 'Thirteenth', label: 'Thirteenth' },
    { key: 'Fourteenth', label: 'Fourteenth' },
    { key: 'Fifteenth', label: 'Fifteenth' },
    { key: 'Sixteenth', label: 'Sixteenth' },
    { key: 'Seventeenth', label: 'Seventeenth' },
    { key: 'Eighteenth', label: 'Eighteenth' },
    { key: 'Nineteenth', label: 'Nineteenth' },
    { key: 'Twentieth', label: 'Twentieth' },
  ];

  constructor(@Inject(ENV) private readonly env: Environment) {}

  getTimezoneKey(label: string) {
    const timezone = this.timeZones.find((tz) => {
      return tz.label === label;
    });

    return timezone ? timezone.key : '';
  }

  getTimezones() {
    return this.timeZones;
  }

  getStates() {
    return this.states;
  }

  getEventResults() {
    return this.eventResults;
  }

  getDateTimeWithTimezoneAbbr(dateTime: string, timeZone: string) {
    return `${moment(dateTime).tz(timeZone).format(this.env.moment.format)} ${moment
      .tz(dateTime, timeZone)
      .zoneAbbr()}`;
  }

  getDateTime(dateTime: string, timeZone: string) {
    return moment(dateTime).tz(timeZone).format(this.env.moment.format);
  }

  getDateTimeMoment(dateTime: string, timeZone: string) {
    return moment(dateTime).tz(timeZone);
  }

  getDateTimeCustomFormat(dateTime: string, timeZone: string, format: string) {
    return moment(dateTime).tz(timeZone).format(format);
  }

  getDateTimeToUTCWithCustomFormat(dateTime: string, timeZone: string, format: string) {
    return moment.tz(dateTime, timeZone).utc().format(format);
  }

  getDateTimeISOFormat(dateTime: string | undefined) {
    if (!dateTime) {
      return undefined;
    }
    return moment(dateTime).toISOString();
  }

  getUtcToMoment(iso: string, timezone: string) {
    if (iso && /T.*-\d\d$/.test(iso)) {
      // moment doesn't do a great job of parsing -XX offsets. Like it doesn't do it at all. It wants -XX:00 in order to parse correctly.
      iso += ':00';
    }
    if (timezone) {
      if (timezone == 'UTC') {
        return moment(iso).utc();
      } else {
        return moment.tz(iso, timezone);
      }
    } else {
      return moment(iso);
    }
  }

  getMomentToUtcDate(dateTime: moment.Moment, format: string) {
    if (format) {
      return dateTime.format(format);
    }
    return dateTime.format('MM/DD/YYYY');
  }

  getUtcDateToMoment(date: string) {
    return moment(date);
  }
}
